Returns the paginated list of campaigns for the tenant authenticated by the credential.

## Pagination

- Use `page` to navigate between pages.
- Use `page_size` to control page size.
- The contract follows the standard `count`, `next`, `previous`, and `results` envelope.
- `page` is 1-based.
- `page_size` defaults to `25` and maxes out at `100`.

## Filters and ordering

- `status`: accepts one or more campaign statuses, comma-separated.
- `campaign_type`: accepts one or more campaign types, comma-separated.
- `ai_agent_provider_id`: filters by one or more provider IDs.
- `ai_agent_id`: filters by one or more agent IDs.
- `created_by`: filters by one or more creator user IDs.
- `ordering`: accepts the documented public ordering fields.
- `search`: text search across `name` and `description`.

<ApiCodeExamples exampleId="campaignsList" />

<ApiResponseExamples exampleId="campaignsList" />

## Main fields per item

- `id`: campaign ID.
- `canonical_url`: canonical Contact-Tel app link for the campaign.
- `created_by_id`, `created_by_name`: creator user reference.
- `status`: persisted campaign state.
- `campaign_type`: operational campaign modality.
- `ai_agent_provider_id`, `ai_agent_id`, `sip_trunk_id`: related public resource IDs.
- `ai_agent_provider_name`, `ai_agent_name`, `sip_trunk_name`: display labels.
- `transfer_target_type`, `transfer_target_id`, `transfer_target_name`, `transfer_target_extension`: configured transfer target, when present.
- `total_contacts`, `pending_contacts`, `reached_contacts`: progress counters.
- `total_calls`, `connected_calls`, `converted_calls`: call-level operational view.
- `ai_billed_seconds`, `ai_gross_billed_seconds`, `ai_refunded_seconds`: net, gross and refunded AI consumption.
- `telephony_billed_seconds`, `telephony_gross_billed_seconds`, `telephony_refunded_seconds`: net, gross and refunded telephony consumption.
- `progress_percentage`: progress derived from the campaign counters.

## Option reference

- `status`: `draft` is not ready to run; `processing` is preparing/importing contacts; `ready` can start; `active` is running; `paused` is stopped temporarily; `completed` finished; `cancelled` was cancelled.
- `status_reason`: `import_failed` means import blocked the campaign; `config_error` means configuration blocked execution; `paused` is a normal operational pause; `null` means no extra public reason.
- `campaign_type`: `ai` is an AI-agent campaign; `reverse_ivr` is a Reverse IVR campaign.
- `ai_agent_provider_type`: `cloud`, `asterisk`, or `sip`.
- `transfer_target_type`: `user`, `group`, or `null`.
- `import_status`: `idle`, `processing`, `completed`, or `failed`.

## When to use this endpoint

- list tenant campaigns in external supervision tools;
- power external dashboards;
- monitor operational progress without using the admin UI.
